// THE DEGRADATION CONTRACT (UNIFIED-FUNCTION F5), in one shape.
//
// Shared by the server and both SPAs — no `pg`, no React, no src/server imports.
//
// The law: a composite endpoint NEVER 500s because one of its sources failed, and it NEVER
// silently drops the row it could not compute. It carries `problems[]`, the affected cells
// degrade to the `unknown` tone, and the operator reads which source is down and what that
// costs them. `GET /engagements/gates/summary` dropping rows on failure is the anti-pattern
// this replaces (dashboard §8).

/**
 * One failed sub-source inside a composite payload.
 *
 * `source`  the producer, named the way the operator would name it: 'gates', 'activity',
 *           'portal-projection'. Not a stack frame.
 * `message` the producer's OWN error text, verbatim. B7: no paraphrase, ever.
 * `code`    optional machine hook for a client that wants to route to the fix.
 * `engagement_code` optional attribution when the failure is per-engagement rather than global.
 */
export interface Problem {
  source: string;
  message: string;
  code?: string;
  engagement_code?: string | null;
}

/** Every composite endpoint's minimum envelope. Zones read `problems` before they read data. */
export interface Degradable {
  problems: Problem[];
  /**
   * A TIMESTAMP, not a nonce — the determinism mandate in testable form: a golden-file test
   * strips this field and asserts the rest is byte-identical across two calls.
   */
  generated_at: string;
}

/** True when any listed source failed. Zones use it to pick the `unknown` tone over a figure. */
export const hasProblem = (problems: Problem[], ...sources: string[]): boolean =>
  problems.some((p) => sources.includes(p.source));

/** The one sentence a degraded zone shows. Never invented — it is the producer's own message. */
export function problemSentence(problems: Problem[], source: string): string | null {
  const hit = problems.find((p) => p.source === source);
  return hit ? hit.message : null;
}

/**
 * The three empty states, distinct (F5.5, clients N8 is the canonical spec). They are three
 * different facts about the world and must never share a rendering:
 *
 *   error     the fetch failed. Names the endpoint, states that NOTHING was written, offers retry.
 *   no-match  the fetch succeeded and the filter excluded everything. Names the filter, offers
 *             to clear it.
 *   empty     the fetch succeeded and there is genuinely nothing. States the schema fact.
 */
export type EmptyKind = 'error' | 'no-match' | 'empty';

/**
 * The headline of each kind, and the fixed sentences of the sheet. English, on purpose: the
 * text IS the catalogue key, and the client's `t()` renders it in the operator's language
 * (German in src/client/i18n/problems.de.ts). Shared files register no catalogue of their own.
 */
export const EMPTY_TITLE: Record<EmptyKind, string> = {
  error: 'Could not load',
  'no-match': 'Nothing matches',
  empty: 'Nothing here yet',
};

export const EMPTY_TEXT = {
  /** `error` only — the one sentence that makes an error survivable. */
  nothingWritten: 'Nothing was written.',
  retry: 'Try again',
  clearFilter: 'Clear the filter',
  /** The degraded-zone headline: `{zone}` is what the operator loses, in their words. */
  zoneFailed: '{zone} could not be computed',
  /** The degraded-zone body: the producer's own message, verbatim (B7). */
  sourceAnswered: 'The {source} source answered: “{message}”',
} as const;
